import { RenderMode, StoreItemPath } from 'app/types-shared';
import { SchemaBuilder } from 'app/infrastructure/ui/builder';
import { inferHostSectionOfChildEntity } from 'app/area/case/selectors';
import { getEntityTypePathFromStorePath } from 'app/infrastructure/store/entity';
import { UiDefinition } from 'app/infrastructure/ui/types-uidefinition';

/**
 * For legacy reasons, the first level always maps to tblTrans under the route alias of /draft or /case
 *
 * @param mode RenderMode to resolve for
 */
const mapDataKeyToLocationKey = (mode: RenderMode) => (route: string) => {
  const mappings = {
    tblTrans: (mode === RenderMode.edit ? 'draft' : 'case'),
    id: 'tblTrans'
  };
  const match = Object.keys(mappings).find( key => route.toLowerCase() === key.toLowerCase() );
  return mappings[match] || route;
};

/**
 * Simply map routes to CompositeKeys to be able to deep link to child items.
 * 
 * e.g '/draft/1/TblAction/2' => {tblTrans:1, tblAction: 2}
 * 
 * @param params  Params from e.g react-router
 * @param mode RenderMode to use when resolving legacy route keys
 * @param overrideParams Use to override values in @params. E.g. 
 * read mode of case we need to use the Primary key for tblTrans when route params contains case number
 */
export const storeItemPathFromRoute = (
  params: {[route: string] : string}, 
  mode: RenderMode, 
  overrideParams = {}) => {
  
  const result = [];
  const routes = Object.keys(params);
  const firstRoute = mapDataKeyToLocationKey(mode)(routes[0]);

  // By convention. Push first route item as case (trans).
  result.push( { [firstRoute] : Number( overrideParams[routes[0]] || params[routes[0]]) } );
   
  // Add children of case
  routes.forEach(route => {
    const routeForEntity = mapDataKeyToLocationKey(mode)(route); 
    const childMatches = params[routeForEntity];
    const childEntity = routeForEntity && !routeForEntity.includes('Value') ? childMatches : undefined;
    const childEntityId = childMatches ? params[routeForEntity+'Value'] : undefined;
    
    if (childEntity) { 
      result.push( { [ childEntity ] : Number( childEntityId ) } );
    }
  });

  return result;
};

/**
 * Build a location from a CompositeKeys object. 
 * 
 * E.g `{tblTrans: 123, tblAction: 4} => '/draft/123/TblAction/4`
 *
 * @param paths 
 */
export const routeFromStoreItemPath = (paths: StoreItemPath[], mode: RenderMode) =>  
  Array.isArray(paths) ? 
  paths.reduce(
    (prev: any, path: StoreItemPath, index: number, all: StoreItemPath[]) => {
      const itemKey = Object.keys(path)[0];     
      return [
        ...prev,
        ...[ 
          mapDataKeyToLocationKey(mode)(itemKey), 
          path[itemKey] ]
      ];
    }, ['']).join('/') : ''; 

/**
 * Find the first section associated with an entity. 'Associated' usually means the first section with an iterator for the same entity type.
 */
export const sectionFromStoreItemPath = (entityPath: StoreItemPath[], renderMode: any, uiDefinition: UiDefinition) => {
  if(!entityPath) {
    return;
  }
  
  const schema = SchemaBuilder.create(renderMode, uiDefinition);
  return inferHostSectionOfChildEntity({ sections: schema.availableSections, entityTypePath: getEntityTypePathFromStorePath(entityPath) });
};
